import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Linkedin, Wrench, Zap, Thermometer } from "lucide-react";
import { business, socials, services, DEMO_DISCLAIMER, AGENCY } from "@/data/site";
import { track } from "@/lib/analytics";

const iconMap = { Facebook, Instagram, Linkedin, MapPin };

const Footer = () => {
  return (
    <footer className="bg-[#0F2E4C] text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Brand */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-[#059669] flex items-center justify-center">
              <span className="text-white font-display font-black text-xl">E</span>
            </div>
            <div className="font-display font-black text-lg">Empire Home Services</div>
          </div>
          <p className="text-sm text-slate-300 leading-relaxed mb-4">
            New York's trusted plumbing, electrical & HVAC contractor. Licensed, insured, and available 24/7 for emergencies.
          </p>
          <div className="flex gap-3">
            {socials.map((s) => {
              const Icon = iconMap[s.icon] || MapPin;
              // Do not publish fake links: render disabled placeholder when pending.
              return s.url ? (
                <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" onClick={() => track("social_click", { network: s.name })} aria-label={s.name} className="w-10 h-10 border border-slate-600 flex items-center justify-center hover:bg-[#059669] hover:border-[#059669] transition-colors" data-testid={`social-${s.name.toLowerCase().replace(/\s/g, "-")}`}>
                  <Icon className="w-5 h-5" />
                </a>
              ) : (
                <span key={s.name} title={`${s.name} — add link later`} aria-label={`${s.name} link coming soon`} className="w-10 h-10 border border-slate-700 flex items-center justify-center text-slate-500 cursor-not-allowed" data-testid={`social-${s.name.toLowerCase().replace(/\s/g, "-")}-pending`}>
                  <Icon className="w-5 h-5" />
                </span>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-500 mt-3">LinkedIn & Google Business Profile links are placeholders — add real URLs in <code>src/data/site.js</code>.</p>
        </div>

        {/* Services */}
        <div>
          <h4 className="font-display font-bold uppercase tracking-wide text-sm mb-4 text-[#34D399]">Services</h4>
          <ul className="space-y-2 text-sm text-slate-300">
            {services.map((s) => {
              const Icon = { Wrench, Zap, Thermometer }[s.icon];
              return (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="flex items-center gap-2 hover:text-white transition-colors">
                    <Icon className="w-4 h-4 text-[#059669]" /> {s.name}
                  </Link>
                </li>
              );
            })}
            <li><Link to="/emergency" className="hover:text-white transition-colors">Emergency Service</Link></li>
            <li><Link to="/financing" className="hover:text-white transition-colors">Financing</Link></li>
          </ul>
        </div>

        {/* Company */}
        <div>
          <h4 className="font-display font-bold uppercase tracking-wide text-sm mb-4 text-[#34D399]">Company</h4>
          <ul className="space-y-2 text-sm text-slate-300">
            <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
            <li><Link to="/projects" className="hover:text-white transition-colors">Before & After Work</Link></li>
            <li><Link to="/reviews" className="hover:text-white transition-colors">Reviews</Link></li>
            <li><Link to="/service-areas" className="hover:text-white transition-colors">Service Areas</Link></li>
            <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
            <li><Link to="/contact" className="hover:text-white transition-colors">Request an Estimate</Link></li>
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="font-display font-bold uppercase tracking-wide text-sm mb-4 text-[#34D399]">Contact</h4>
          <ul className="space-y-3 text-sm text-slate-300">
            <li><a href={business.phoneHref} onClick={() => track("phone_tap", { location: "footer" })} className="flex items-center gap-2 hover:text-white" data-testid="footer-phone"><Phone className="w-4 h-4 text-[#059669]" /> {business.phoneDisplay}</a></li>
            <li><a href={business.emailHref} onClick={() => track("email_click", { location: "footer" })} className="flex items-center gap-2 hover:text-white break-all" data-testid="footer-email"><Mail className="w-4 h-4 text-[#059669]" /> {business.email}</a></li>
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 text-[#059669] mt-0.5 shrink-0" /> {business.address}</li>
            <li className="flex items-start gap-2"><Clock className="w-4 h-4 text-[#059669] mt-0.5 shrink-0" />
              <span>{business.hours.map((h) => <span key={h.day} className="block">{h.day}: {h.time}</span>)}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800 bg-[#0A2138]" data-testid="footer-demo-disclaimer">
        <div className="max-w-7xl mx-auto px-4 py-5">
          <p className="text-xs text-slate-400 leading-relaxed">
            <span className="font-bold text-[#F59E0B] uppercase tracking-wide">Demo Site Disclaimer: </span>
            {DEMO_DISCLAIMER}
          </p>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400">
          <div className="text-center sm:text-left">
            <p>© {new Date().getFullYear()} {business.name} (fictional demo). {business.license}</p>
            <p className="mt-1">
              Designed &amp; Built by{" "}
              {AGENCY.url ? (
                <a href={AGENCY.url} target="_blank" rel="noopener noreferrer" onClick={() => track("agency_click", {})} className="font-bold text-[#34D399] hover:text-white transition-colors" data-testid="footer-agency-link">{AGENCY.name}</a>
              ) : (
                <span className="font-bold text-[#34D399]" data-testid="footer-agency-link">{AGENCY.name}</span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center">
            <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms & Conditions</Link>
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/accessibility" className="hover:text-white transition-colors" data-testid="footer-accessibility">Accessibility</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
